import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { createMapInstance, createPinMarkerElement } from '../../utils/maplibreConfig';

const ReadOnlyMap = ({ 
  latitude, 
  longitude, 
  address, 
  height = 300,
  zoom = 15,
  showAddress = true 
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Vérifier si nous avons des coordonnées valides
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      return;
    }

    // Ne créer la carte que si elle n'existe pas déjà
    if (!mapInstanceRef.current && mapRef.current) {
      try {
        const map = createMapInstance({
          container: mapRef.current,
          center: [longitude, latitude],
          zoom,
          pitch: 18,
          dragPan: true,
          touchZoomRotate: true,
          doubleClickZoom: true,
          scrollZoom: true,
          boxZoom: false,
          keyboard: false,
        });

        map.on('load', () => {
          const el = createPinMarkerElement({
            accentColor: '#2563eb',
            innerColor: '#eff6ff',
            size: 20,
            shadowColor: 'rgba(37,99,235,0.28)',
          });

          const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat([longitude, latitude])
            .addTo(map);

          // Popup avec l'adresse si fournie
          if (address) {
            marker.setPopup(
              new maplibregl.Popup({ offset: 14 }).setHTML(`
                <div style="font-family: Roboto, sans-serif; text-align: center;">
                  <strong>📍 Emplacement</strong><br/>
                  <span style="color: #666; font-size: 0.9em;">${address}</span>
                </div>
              `)
            );
            marker.togglePopup();
          }

          // Cercle indicatif de zone — source GeoJSON + layer circle
          map.addSource('zone', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [longitude, latitude] },
              properties: {},
            },
          });
          map.addLayer({
            id: 'zone-fill',
            type: 'circle',
            source: 'zone',
            paint: {
              'circle-radius': {
                stops: [
                  [0,  2],
                  [10, 20],
                  [15, 80],
                  [20, 200],
                ],
                base: 2,
              },
              'circle-color': '#1976d2',
              'circle-opacity': 0.1,
              'circle-stroke-color': '#1976d2',
              'circle-stroke-width': 1,
              'circle-stroke-opacity': 0.4,
            },
          });
        });

        mapInstanceRef.current = map;
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de la carte:', error);
      }
    }

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [latitude, longitude, zoom, address]);

  // Si pas de coordonnées, afficher un message
  if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
    return (
      <Box 
        sx={{ 
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          border: '2px dashed #ccc'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          📍 Localisation non disponible
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {showAddress && address && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
            📍 {address}
          </Typography>
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          height,
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: 2,
          border: '1px solid #e0e0e0',
          '& .maplibregl-map': {
            height: '100%',
            width: '100%'
          },
          '& .maplibregl-ctrl-zoom-in, & .maplibregl-ctrl-zoom-out': {
            border: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          },
          '& .maplibregl-ctrl-zoom-in:hover, & .maplibregl-ctrl-zoom-out:hover': {
            backgroundColor: '#f5f5f5'
          }
        }}
      />
      
      {/* Overlay d'information */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 1,
          px: 1.5,
          py: 0.5,
          fontSize: '0.75rem',
          color: 'text.secondary',
          boxShadow: 1
        }}
      >
        Position indicative
      </Box>
    </Box>
  );
};

export default ReadOnlyMap;
